/**
 * Teams CRUD UI. Allows admins to create teams and add/remove users.
 * Lives inside the Organization page, alongside the Users panel.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography, Button, Table, Space, Tag, Popconfirm,
  message, Spin, Empty, Modal, Form, Input, Drawer, List, AutoComplete,
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, TeamOutlined, UserAddOutlined } from '@ant-design/icons';
import { EXECUTOR_API } from '../../types';
import { useUser, UserRole } from '../../contexts/UserContext';
import { buildAuthHeaders } from '../../utils/userStore';
import { useCrudWithMembers } from '../../hooks/useCrudWithMembers';

const { Text } = Typography;

interface TeamSummary {
  id: string;
  name: string;
  description: string | null;
  members_count: number;
  created_at: string;
  updated_at: string;
}

interface TeamDetail extends TeamSummary {
  members: string[];
}

interface UserOption {
  email: string;
  role: UserRole;
}

const TeamsManagementPanel: React.FC = () => {
  const { email: currentEmail } = useUser();

  const config = useMemo(
    () => ({
      basePath: `${EXECUTOR_API}/agent/builder/teams`,
      listField: 'teams',
      getKey: (t: TeamSummary) => t.id,
      errorMessages: {
        list: 'Error al cargar equipos',
        create: 'Error al crear equipo',
        update: 'Error al actualizar',
        delete: 'Error al eliminar',
        addMember: 'No se pudo añadir el miembro',
      },
    }),
    []
  );
  const {
    items: teams, loading, fetchDetail, create, update, remove, addMember, removeMember,
  } = useCrudWithMembers<TeamSummary, TeamDetail>(config);

  const [users, setUsers] = useState<UserOption[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<TeamDetail | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [memberInput, setMemberInput] = useState('');
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchUsers = useCallback(async () => {
    if (!currentEmail) return;
    try {
      const res = await fetch(`${EXECUTOR_API}/agent/builder/users`, {
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      });
      if (res.ok) {
        const data = await res.json() as { users: UserOption[] };
        setUsers(data.users || []);
      }
    } catch {
      // Non-fatal — the user can still type emails manually
    }
  }, [currentEmail]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (values: { name: string; description?: string }) => {
    const created = await create(values);
    if (created) {
      message.success(`Equipo "${values.name}" creado`);
      setCreateOpen(false);
      form.resetFields();
    }
  };

  const openEdit = async (team: TeamSummary) => {
    setDrawerLoading(true);
    const detail = await fetchDetail(team.id);
    if (detail) {
      setEditingTeam(detail);
      editForm.setFieldsValue({ name: detail.name, description: detail.description || '' });
    } else {
      message.error('Error al cargar el equipo');
    }
    setDrawerLoading(false);
  };

  const handleUpdateDetails = async (values: { name: string; description?: string }) => {
    if (!editingTeam) return;
    const ok = await update(editingTeam.id, values);
    if (ok) {
      message.success('Equipo actualizado');
      setEditingTeam({ ...editingTeam, name: values.name, description: values.description || null });
    }
  };

  const handleDelete = async (team: TeamSummary) => {
    const ok = await remove(team);
    if (ok) message.success(`Equipo "${team.name}" eliminado`);
  };

  const handleAddMember = async () => {
    if (!editingTeam || !memberInput.trim()) return;
    setAddingMember(true);
    const email = memberInput.trim().toLowerCase();
    const ok = await addMember(editingTeam.id, { email });
    if (ok) {
      setEditingTeam({ ...editingTeam, members: [...editingTeam.members, email].sort() });
      setMemberInput('');
    }
    setAddingMember(false);
  };

  const handleRemoveMember = async (email: string) => {
    if (!editingTeam) return;
    const ok = await removeMember(editingTeam.id, email);
    if (ok) {
      setEditingTeam({ ...editingTeam, members: editingTeam.members.filter((m) => m !== email) });
    }
  };

  const availableUsers = users
    .filter((u) => !editingTeam?.members.includes(u.email))
    .map((u) => ({ value: u.email, label: u.email }));

  const columns = [
    {
      title: 'Nombre', dataIndex: 'name', key: 'name',
      render: (name: string, record: TeamSummary) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          {record.description && <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>}
        </Space>
      ),
    },
    {
      title: 'Miembros', dataIndex: 'members_count', key: 'members_count', width: 120,
      render: (n: number) => <Tag icon={<TeamOutlined />}>{n}</Tag>,
    },
    {
      title: '', key: 'actions', width: 100,
      render: (_: unknown, record: TeamSummary) => (
        <Space size="small">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="¿Eliminar este equipo?"
            description="Los miembros se desasocian. Esta acción no se puede deshacer."
            onConfirm={() => handleDelete(record)}
            okText="Eliminar" cancelText="Cancelar" okButtonProps={{ danger: true }}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text type="secondary" style={{ flex: 1 }}>
          Agrupa usuarios en equipos lógicos. Un usuario puede pertenecer a varios equipos.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Nuevo equipo
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : teams.length === 0 ? (
        <Empty description="Aún no hay equipos">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Crear el primero
          </Button>
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={teams.map((t) => ({ ...t, key: t.id }))}
          pagination={false} size="middle"
        />
      )}

      {/* Create team modal */}
      <Modal
        title="Nuevo equipo"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); form.resetFields(); }}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Nombre" rules={[{ required: true, max: 100 }]}>
            <Input placeholder="Ej: Ventas Madrid" autoFocus />
          </Form.Item>
          <Form.Item name="description" label="Descripción (opcional)" rules={[{ max: 500 }]}>
            <Input.TextArea rows={3} placeholder="Para qué sirve este equipo" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateOpen(false); form.resetFields(); }}>Cancelar</Button>
              <Button type="primary" htmlType="submit">Crear equipo</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit team + manage members drawer */}
      <Drawer
        title={editingTeam ? `Equipo: ${editingTeam.name}` : ''}
        open={!!editingTeam}
        onClose={() => setEditingTeam(null)}
        width={520}
        destroyOnClose
      >
        {drawerLoading || !editingTeam ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : (
          <>
            <Form form={editForm} layout="vertical" onFinish={handleUpdateDetails}>
              <Form.Item name="name" label="Nombre" rules={[{ required: true, max: 100 }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description" label="Descripción" rules={[{ max: 500 }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit">Guardar cambios</Button>
              </Form.Item>
            </Form>

            <div style={{ marginTop: 8, marginBottom: 12 }}>
              <Text strong style={{ fontSize: 14 }}>Miembros ({editingTeam.members.length})</Text>
            </div>

            <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
              <AutoComplete
                value={memberInput}
                onChange={setMemberInput}
                options={availableUsers}
                placeholder="email@cubicup.com"
                filterOption={(input, opt) =>
                  (opt?.value as string)?.toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddMember();
                }}
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                loading={addingMember}
                onClick={handleAddMember}
                disabled={!memberInput.trim()}
              >
                Añadir
              </Button>
            </Space.Compact>

            {editingTeam.members.length === 0 ? (
              <Empty description="Sin miembros todavía" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                size="small"
                bordered
                dataSource={editingTeam.members}
                renderItem={(email) => (
                  <List.Item
                    actions={[
                      <Popconfirm
                        key="remove"
                        title="¿Quitar este miembro del equipo?"
                        onConfirm={() => handleRemoveMember(email)}
                        okText="Quitar" cancelText="Cancelar"
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>,
                    ]}
                  >
                    {email}
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default TeamsManagementPanel;
